import { ArrowRight } from 'lucide-react';

const decisionFlow = [
    'Student signals',
    'Skill inference',
    'Impact estimation',
    'Practice selection',
    'Feedback update',
];

const systemReasons = [
    'Learning signals are sparse and noisy, yet decisions must be actionable.',
    'Knowing what a student struggles with is insufficient without estimating impact.',
    'Time is limited; practice must be optimized for learning gain, not quantity.',
];

const SystemOverviewSection = () => {
    return (
        <section className="pt-10 pb-16 md:pt-12 md:pb-24 bg-slate-50/70">
            <div className="container mx-auto max-w-2xl px-6">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-5 md:mb-6">
                    How the system makes decisions
                </h2>

                <p className="text-base md:text-lg text-foreground/80 text-center max-w-xl mx-auto mb-10 md:mb-12 leading-relaxed">
                    Pinpoint Edu is built as a <strong className="text-foreground font-semibold">closed-loop learning system</strong>. It produces an initial, <strong className="text-foreground font-semibold">actionable plan</strong> quickly, then continuously refines diagnosis and priorities as more student work is observed—making practice <strong className="text-foreground font-semibold">increasingly precise and efficient</strong> over time.
                </p>

                {/* Decision Flow */}
                <div className="flex flex-wrap justify-center items-center gap-3 md:gap-4 mb-14 md:mb-16">
                    {decisionFlow.map((step, index) => (
                        <div key={step} className="flex items-center gap-3 md:gap-4">
                            <span className="px-4 py-2.5 bg-white border border-primary/20 rounded-lg text-[15px] md:text-base font-semibold text-foreground shadow-sm min-w-[130px] text-center">
                                {step}
                            </span>
                            {index < decisionFlow.length - 1 && (
                                <ArrowRight className="w-4 h-4 text-primary/40 hidden sm:block" />
                            )}
                        </div>
                    ))}
                </div>

                {/* Why decision quality improves over time - merged section */}
                <div className="max-w-md mx-auto">
                    <h3 className="text-sm font-semibold text-foreground/70 mb-4 text-center uppercase tracking-wide">
                        Why decision quality improves over time
                    </h3>
                    <ul className="space-y-3 mb-6">
                        {systemReasons.map((reason) => (
                            <li key={reason} className="flex items-start gap-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-2.5 shrink-0" />
                                <span className="text-base text-muted-foreground leading-relaxed">{reason}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="text-base text-muted-foreground/80 text-center leading-relaxed">
                        As more responses are observed, skill estimates tighten, priorities reshuffle, and practice becomes increasingly focused and efficient.
                    </p>
                </div>

                {/* Boundary statement */}
                <p className="text-sm text-muted-foreground/50 text-center mt-8 max-w-sm mx-auto italic leading-relaxed">
                    The system focuses on decision quality and learning efficiency, not content volume or test-taking tricks.
                </p>
            </div>
        </section>
    );
};

export default SystemOverviewSection;
